// Pont entre le Canvas de l'éditeur (frontend/src/pages/admin/ThemeEditor/Canvas.jsx)
// et la vraie page d'accueil chargée dans son iframe (même origine, ?studio_preview=1).
// Le rendu de l'iframe est TOUJOURS la vraie boutique — ce module se contente
// d'y appliquer un état "brouillon" éphémère (jamais persisté) reçu par
// postMessage, et de faire remonter les survols/clics de sections au parent.
// Ne s'active JAMAIS pour un vrai visiteur : tout est gardé par isStudioPreview().
import applyPalette from "./applyPalette";
import applyTypography from "./applyTypography";
import applyShape from "./applyShape";
import applySpacing from "./applySpacing";
import applyControls from "./applyControls";

const SOURCE_PARENT = "theme-editor";
const SOURCE_PREVIEW = "theme-preview";

export function isStudioPreview() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("studio_preview") === "1";
}

// Dans l'iframe du Canvas, le premier fetch (avant même l'arrivée du
// brouillon par postMessage) doit déjà résoudre le BON thème — et c'est le
// SEUL mécanisme disponible du tout pour un onglet de prévisualisation
// ouvert en navigation directe (Aperçu depuis Boutique en ligne/le
// sélecteur de thèmes) : là, il n'y a pas de fenêtre parente pour envoyer
// un brouillon par postMessage. Sans ces paramètres, les 4 endpoints
// publics /theme/* retombent silencieusement sur le thème actif (voir
// resolve_public_theme côté backend) au lieu du thème demandé.
export function previewQueryParams() {
  if (!isStudioPreview()) return {};
  const sp = new URLSearchParams(window.location.search);
  const theme_id = sp.get("theme_id");
  const preview_token = sp.get("preview_token");
  return theme_id && preview_token ? { theme_id, preview_token } : {};
}

// Origine de la fenêtre PARENTE (l'admin) — transmise explicitement dans
// l'URL par Canvas.jsx (`admin_origin`) plutôt que supposée égale à
// `window.location.origin` : depuis qu'un superadmin multi-boutiques peut
// éditer une boutique dont le domaine diffère de celui de l'admin, cette
// iframe est potentiellement CROSS-ORIGIN par rapport à sa page parente.
// Repli sur `window.location.origin` (comportement d'origine, same-origin)
// quand le paramètre est absent — ex: un onglet de prévisualisation ouvert
// en navigation directe, sans fenêtre parente à qui répondre.
function parentOrigin() {
  const sp = new URLSearchParams(window.location.search);
  return sp.get("admin_origin") || window.location.origin;
}

let currentSelectedKey = null;
let currentSelectedBlockKey = null;

// Sprint 5 — sélection au niveau BLOC (les cartes individuelles d'une
// section, ex: "Nos expertises") en plus de la sélection de section entière
// déjà existante. Un bloc est TOUJOURS imbriqué dans sa section : `closest`
// avec les deux sélecteurs combinés renvoie naturellement l'élément le plus
// proche de la souris/du clic, donc le bloc avant la section englobante,
// sans logique de priorité explicite à écrire.
function keysFromElement(el) {
  if (!el) return { key: null, blockKey: null };
  if (el.hasAttribute("data-block-key")) {
    const sectionEl = el.closest("[data-section-key]");
    return { key: sectionEl?.getAttribute("data-section-key") || null, blockKey: el.getAttribute("data-block-key") };
  }
  return { key: el.getAttribute("data-section-key"), blockKey: null };
}

function isCurrentlySelected(key, blockKey) {
  return blockKey ? blockKey === currentSelectedBlockKey : key === currentSelectedKey && !currentSelectedBlockKey;
}

function highlightSelected(key, blockKey) {
  currentSelectedKey = key;
  currentSelectedBlockKey = blockKey || null;
  // Une section n'est surlignée en entier que si AUCUN bloc précis n'y est
  // sélectionné — sélectionner une carte met en avant CETTE carte seule,
  // comme dans Shopify, plutôt que toute la section qui la contient.
  document.querySelectorAll("[data-section-key]").forEach((el) => {
    const isSelected = el.getAttribute("data-section-key") === key && !currentSelectedBlockKey;
    el.style.outline = isSelected ? "2px solid #2c6ecb" : "";
    el.style.outlineOffset = isSelected ? "-2px" : "";
  });
  document.querySelectorAll("[data-block-key]").forEach((el) => {
    const isSelected = !!currentSelectedBlockKey && el.getAttribute("data-block-key") === currentSelectedBlockKey;
    el.style.outline = isSelected ? "2px solid #2c6ecb" : "";
    el.style.outlineOffset = isSelected ? "-2px" : "";
  });
}

function postToParent(type, extra = {}) {
  if (window.parent === window) return;
  window.parent.postMessage({ source: SOURCE_PREVIEW, type, ...extra }, parentOrigin());
}

let lastHoveredEl = null;

function bindDomBridge() {
  // mouseover/mouseout bubblent et se redéclenchent à chaque sous-élément
  // survolé (contrairement à mouseenter/mouseleave, qui ne délèguent pas) —
  // on ne réagit donc que lorsque l'élément le plus proche (closest, bloc OU
  // section) change réellement, pour éviter tout scintillement/spam de
  // postMessage.
  document.addEventListener("mouseover", (e) => {
    const el = e.target.closest("[data-block-key], [data-section-key]");
    if (el === lastHoveredEl) return;
    if (lastHoveredEl) {
      const prev = keysFromElement(lastHoveredEl);
      if (!isCurrentlySelected(prev.key, prev.blockKey)) lastHoveredEl.style.outline = "";
    }
    lastHoveredEl = el;
    const { key, blockKey } = keysFromElement(el);
    if (el) {
      if (!isCurrentlySelected(key, blockKey)) {
        el.style.outline = "1px dashed #8c9196";
        el.style.outlineOffset = "-1px";
      }
      postToParent("hover", { key, blockKey });
    } else {
      postToParent("hover", { key: null, blockKey: null });
    }
  });

  document.addEventListener(
    "click",
    (e) => {
      // Lien INTERNE (menu de l'en-tête, bouton « Découvrir notre
      // savoir-faire », lien du pied de page…) : jamais suivi dans l'iframe.
      // Une navigation y perdrait les paramètres d'aperçu de l'URL
      // (?studio_preview=1&theme_id=…&preview_token=…) : la page suivante
      // afficherait le thème ACTIF de la boutique — pour une page propre au
      // moteur du brouillon (ex: /savoir-faire), une « Page introuvable ».
      // Le Studio, lui, sait ouvrir la page visée dans son sélecteur de page
      // (voir Canvas.jsx, message "navigate").
      //
      // Uniquement pour les liens de l'EN-TÊTE et du PIED DE PAGE (ou hors de
      // toute section) : un lien dans une section de contenu (« Voir la
      // boutique » d'un bandeau…) garde son rôle d'origine dans le Canvas,
      // sélectionner cette section pour l'éditer — inchangé pour tous les
      // thèmes existants.
      const link = e.target.closest("a[href]");
      const owner = link && link.closest("[data-section-key]");
      const ownerKey = owner ? owner.getAttribute("data-section-key") : null;
      const inChrome = !owner || ownerKey === "site_header" || ownerKey === "site_footer";
      if (link && inChrome) {
        let url = null;
        try { url = new URL(link.href, window.location.href); } catch { url = null; }
        if (url && url.origin === window.location.origin) {
          const samePage = url.pathname === window.location.pathname;
          if (samePage && url.hash) {
            // Ancre de la page affichée (« #contact ») : le navigateur fait
            // défiler l'aperçu, la requête reste intacte.
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          postToParent("navigate", { path: url.pathname, hash: url.hash });
          return;
        }
      }
      const el = e.target.closest("[data-block-key], [data-section-key]");
      if (!el) return;
      // On empêche toute navigation/soumission (liens, boutons du contenu
      // prévisualisé) : dans le Canvas, cliquer sert à SÉLECTIONNER un bloc
      // pour l'éditer, jamais à interagir avec la boutique comme un visiteur.
      e.preventDefault();
      e.stopPropagation();
      const { key, blockKey } = keysFromElement(el);
      postToParent("select", { key, blockKey });
    },
    true
  );
}

function applyDraft(payload) {
  if (!payload) return;
  if (payload.palette) applyPalette(payload.palette);
  if (payload.typography) applyTypography(payload.typography);
  if (payload.shape) applyShape(payload.shape);
  if (payload.spacing) applySpacing(payload.spacing);
  if (payload.controls) applyControls(payload.controls);
  if (payload.branding) window.dispatchEvent(new CustomEvent("studio:branding", { detail: payload.branding }));
  // `template` (Sprint 11) précise à QUEL domaine appartiennent ces sections
  // (home / header / footer...) — Home.jsx, Header.jsx et Footer.jsx
  // écoutent tous "studio:sections" mais n'appliquent que l'évènement
  // correspondant à LEUR propre template, jamais celui d'un autre.
  if (payload.sections) {
    window.dispatchEvent(new CustomEvent("studio:sections", {
      detail: { template: payload.template || "home", sections: payload.sections },
    }));
  }
  highlightSelected(payload.selectedKey || null, payload.selectedBlockKey || null);
}

// Une section (ou un bloc) VIENT D'ÊTRE AJOUTÉE : le parent envoie son
// brouillon puis, aussitôt, l'ordre de recentrer l'aperçu dessus. L'élément
// correspondant n'existe pas encore dans le DOM à cet instant (le brouillon
// vient d'être appliqué, React n'a pas fini de rendre), donc un simple
// querySelector échouait silencieusement : l'aperçu restait en haut de page
// et donnait l'impression que la section ajoutée n'était pas affichée du
// tout. On réessaie donc brièvement jusqu'à ce qu'elle soit rendue.
function scrollToWhenRendered(selector, attempt = 0) {
  const el = document.querySelector(selector);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  if (attempt < 20) setTimeout(() => scrollToWhenRendered(selector, attempt + 1), 75);
}

export function initStudioPreviewBridge() {  if (!isStudioPreview()) return;
  window.__STUDIO_PREVIEW__ = true;

  window.addEventListener("message", (event) => {
    if (event.origin !== parentOrigin()) return;
    if (event.source !== window.parent) return;
    const msg = event.data;
    if (!msg || msg.source !== SOURCE_PARENT) return;
    if (msg.type === "draft") applyDraft(msg.payload);
    if (msg.type === "scroll-to") {
      const selector = msg.blockKey ? `[data-block-key="${msg.blockKey}"]` : `[data-section-key="${msg.key}"]`;
      scrollToWhenRendered(selector);
    }
  });

  bindDomBridge();
  // Signale au parent que ce document est prêt à recevoir un brouillon —
  // évite une course où le parent enverrait un message avant que ce
  // listener n'existe encore.
  postToParent("ready");
}
